import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Palette, Printer, Monitor, Box, Smartphone } from 'lucide-react';

// Hero Video Section
export const HeroVideoSection = ({ showHeroText, setShowHeroText, theme }) => {
  const [introVisible, setIntroVisible] = useState(true);
  const [introFading, setIntroFading] = useState(false);
  const [preLoopBlack, setPreLoopBlack] = useState(false);
  const [showLogos, setShowLogos] = useState(false);
  const fadeTimerRef = useRef(null);
  const removeTimerRef = useRef(null);
  const hasLoopedRef = useRef(false);
  const preLoopRef = useRef(false);

  const PLACEHOLDER_LOGOS = [
    { id: 1,  name: 'Chelsea FC',      src: 'https://spiralcreations.co.uk/Chelsea_FC.svg' },
    { id: 2,  name: 'West Ham',        src: 'https://spiralcreations.co.uk/West_Ham_United_FC.png' },
    { id: 3,  name: 'Tottenham',       src: 'https://spiralcreations.co.uk/tottenham.svg' },
    { id: 4,  name: 'Fulham FC',       src: 'https://spiralcreations.co.uk/Fulham_FC.png' },
    { id: 5,  name: "Guns N' Roses",   src: '/gnr_logo.png' },
    { id: 6,  name: 'EA Sports',       src: 'https://static.wikia.nocookie.net/logopedia/images/b/b4/EA_Sports_%28Flat%29.svg' },
    { id: 7,  name: 'Halfords',        src: 'https://images.seeklogo.com/logo-png/51/2/halfords-logo-png_seeklogo-518458.png' },
    { id: 8,  name: 'Superdrug',       src: '/superdrug_logo.png', cover: true },
    { id: 9,  name: 'Jewlr',           src: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTRq7yUR2A6ehwTMksDEMGwNqoxhbS9_IjtGQ&s' },
    { id: 11, name: 'Sinclair London', src: '/sinclair.webp' },
    { id: 12, name: 'Skepta',          src: 'https://i.namu.wiki/i/AXzXdyAgHswIpN7N6pAsRsKJlGGiqTs77w_-_oPzmCN-GsjOAlQtM4pUM6p6yR-qPXhNcuFJUctiMS21OEoiMw.webp' },
    { id: 13, name: 'Bromley FC',      src: '/bromley_fc.svg' },
    { id: 14, name: 'NFL',             src: '/nfl.png' },
  ];

  const triggerIntro = () => {
    setIntroFading(false);
    setIntroVisible(true);
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    if (removeTimerRef.current) clearTimeout(removeTimerRef.current);
    fadeTimerRef.current = setTimeout(() => setIntroFading(true), 2500);
    removeTimerRef.current = setTimeout(() => setIntroVisible(false), 4300);
  };

  useEffect(() => {
    triggerIntro();
    return () => {
      clearTimeout(fadeTimerRef.current);
      clearTimeout(removeTimerRef.current);
    };
  }, []);

  return (
    <div className="mb-16">
      <div className={`${theme.card} backdrop-blur-sm border rounded-2xl overflow-hidden relative`}>
        <video
          className="w-full h-64 sm:h-80 md:h-96 object-cover"
          autoPlay
          loop
          muted
          playsInline
          src="/hero-video.mp4"
          onTimeUpdate={(e) => {
            const t = e.target.currentTime;
            const dur = e.target.duration;
            // Fade to black 1.5s before loop
            if (dur && t > dur - 1.2 && !preLoopRef.current) {
              preLoopRef.current = true;
              setPreLoopBlack(true);
            }
            // On loop reset
            if (t < 0.5 && hasLoopedRef.current) {
              hasLoopedRef.current = false;
              preLoopRef.current = false;
              setPreLoopBlack(false);
              triggerIntro();
              setShowLogos(false);
              setShowHeroText(false);
            }
            if (dur && t > dur - 1) hasLoopedRef.current = true;
            if (t >= 3 && t < 6 && !showLogos) setShowLogos(true);
            if ((t < 3 || t >= 6) && showLogos) setShowLogos(false);
            if (t >= 6 && !showHeroText) setShowHeroText(true);
            if (t < 1 && showHeroText) setShowHeroText(false);
          }}
        >
          <div className={`w-full h-64 sm:h-80 md:h-96 ${theme.isDarkMode ? 'bg-slate-700/50' : 'bg-gray-200'} flex items-center justify-center`}>
            <p className={`${theme.text.muted} font-sans`}>Video not supported</p>
          </div>
        </video>

        {/* Pre-loop fade to black */}
        <div
          className="absolute inset-0 bg-black pointer-events-none"
          style={{
            opacity: preLoopBlack ? 1 : 0,
            transition: 'opacity 1s ease-in-out',
            zIndex: 9
          }}
        />

        {/* Black intro overlay with GIANTS text */}
        {introVisible && (
          <div
            className="absolute inset-0 bg-black flex items-center justify-center"
            style={{
              opacity: introFading ? 0 : 1,
              transition: introFading ? 'opacity 1.8s ease-in-out' : 'opacity 0.8s ease-in-out',
              pointerEvents: introFading ? 'none' : 'auto',
              zIndex: 10
            }}
          >
            <div className="text-center px-4 relative flex flex-col items-center justify-center">
              {/* Animated pink spiral */}
              <svg
                viewBox="0 0 300 300"
                className="absolute"
                style={{
                  width: 'clamp(200px, 50vw, 420px)',
                  height: 'clamp(200px, 50vw, 420px)',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none'
                }}
              >
                <defs>
                  <style>{`
                    @keyframes spiral-draw {
                      from { stroke-dashoffset: 2400; opacity: 0; }
                      to { stroke-dashoffset: 0; opacity: 0.55; }
                    }
                    @keyframes spiral-rotate {
                      from { transform-origin: 150px 150px; transform: rotate(0deg); }
                      to { transform-origin: 150px 150px; transform: rotate(360deg); }
                    }
                    .spiral-g { animation: spiral-rotate 8s linear infinite; }
                    .spiral-path {
                      fill: none;
                      stroke: #eb008b;
                      stroke-width: 3.5;
                      stroke-linecap: round;
                      stroke-dasharray: 2400;
                      stroke-dashoffset: 2400;
                      animation: spiral-draw 1.4s cubic-bezier(0.4,0,0.2,1) 0.4s forwards;
                    }
                  `}</style>
                </defs>
                <g className="spiral-g">
                  <path className="spiral-path" d={(() => {
                    let d = 'M 150 150 ';
                    const turns = 5;
                    const points = turns * 60;
                    for (let i = 1; i <= points; i++) {
                      const angle = (i / 60) * 2 * Math.PI;
                      const r = (i / points) * 120;
                      const x = 150 + r * Math.cos(angle);
                      const y = 150 + r * Math.sin(angle);
                      d += `L ${x.toFixed(2)} ${y.toFixed(2)} `;
                    }
                    return d;
                  })()} />
                </g>
              </svg>
              <p
                className="text-gray-300 font-sans tracking-[0.4em] uppercase mb-0 font-semibold relative z-10"
                style={{
                  fontSize: 'clamp(0.9rem, 2.5vw, 1.3rem)',
                  animation: 'trusted-fade-in 0.8s cubic-bezier(0.16,1,0.3,1) 0.2s both'
                }}
              >
                Trusted by
              </p>
              <h1
                className="font-sans font-black uppercase text-white relative z-10"
                style={{
                  fontSize: 'clamp(3rem, 10vw, 7rem)',
                  letterSpacing: '0.1em',
                  marginTop: '-0.15em',
                  animation: 'giants-fade-in 1s cubic-bezier(0.16,1,0.3,1) 0.6s both'
                }}
              >
                GIANTS
              </h1>
            </div>
          </div>
        )}

        {/* Logo single-row overlay */}
        <div
          className="absolute inset-0 pointer-events-none overflow-hidden flex flex-col items-center justify-center gap-6"
          style={{ opacity: showLogos ? 1 : 0, transition: 'opacity 0.8s ease-in-out' }}
        >
          <div className="text-center px-4">
            <p className="text-white/80 font-sans tracking-[0.4em] uppercase font-semibold" style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.3rem)' }}>Proudly working with</p>
          </div>

          {/* Single row scrolling right */}
          <div className="w-full overflow-hidden">
            <div className="flex gap-2 sm:gap-4 logo-row-right">
              {[...PLACEHOLDER_LOGOS.slice().reverse(), ...PLACEHOLDER_LOGOS.slice().reverse()].map((logo, i) => (
                <div key={i} className="flex-shrink-0 w-12 h-12 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center"
                  style={{
                    border: '1px solid rgba(255,255,255,0.25)',
                    background: 'rgba(0,0,0,0.4)',
                    padding: '6px'
                  }}
                >
                  <div className="w-full h-full rounded-sm overflow-hidden flex items-center justify-center">
                    <img src={logo.src} alt={logo.name}
                      className={`${logo.cover ? 'w-full h-full object-cover' : 'max-w-full max-h-full object-contain'}`}
                      style={{ opacity: 0.7 }}
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Existing text overlay */}
        <div className={`absolute inset-0 transition-all duration-2000 ease-in-out ${
          showHeroText ? 'opacity-100' : 'opacity-0'
        } flex items-center justify-center`}
          style={{
            backgroundImage: 'url(/og-image.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}>
          <div className="absolute inset-0 bg-black/60" />
          {/* Vignette */}
          <div className="absolute inset-0" style={{background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.85) 100%)'}} />
          <div className={`relative text-center transition-all duration-1000 delay-500 px-4 ${
            showHeroText ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'
          }`} style={{textShadow: '0 2px 20px rgba(0,0,0,1), 0 4px 40px rgba(0,0,0,0.9)'}}>
            <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-2 sm:mb-4 font-sans">
              You dream, we create!
            </h1>
            <p className="text-sm sm:text-lg md:text-xl text-gray-200 font-sans max-w-2xl">
              Bringing ideas to life through creative excellence
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

// Services Section
export const ServicesSection = ({ theme, scrollToContact }) => {
  return (
    <div id="services" className="mb-16" style={{scrollMarginTop: '120px'}}>
      <div className="text-center mb-12">
        <h2 className={`text-xl sm:text-2xl font-bold ${theme.text.primary} mb-1 font-sans title-animate`}>
          Our Services
        </h2>
        <p className={`text-xs ${theme.text.muted} font-sans flex items-center justify-center subtitle-animate`}>
          Complete creative solutions for your business
        </p>
        <div className="h-0.5 mx-auto mt-2 rounded-full underline-animate" style={{backgroundColor: '#eb008b'}}></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Design Services */}
        <div className={`portfolio-item ${theme.card} backdrop-blur-sm border rounded-2xl p-6 group transition-all duration-500 hover:shadow-2xl hover:border-pink-500/30`} style={{ opacity: 0 }}>
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-pink-500/20 to-pink-600/20 rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
              <Palette size={24} className="text-pink-400" />
            </div>
            <h3 className={`text-lg font-bold ${theme.text.primary} font-sans`}>Creative</h3>
          </div>
          <p className={`${theme.text.muted} text-sm mb-4 font-sans`}>
            Complete visual identity & multimedia solutions
          </p>
          <ul className={`space-y-2 text-sm ${theme.text.secondary} font-sans`}>
            <li>• Logo design & branding</li>
            <li>• Artworking & retouching</li>
            <li>• Publishing & layout</li>
            <li>• Brand identity systems</li>
            <li>• Video editing & post-production</li>
            <li>• Motion graphics & animation</li>
          </ul>
        </div>

        {/* Print Services */}
        <div className={`portfolio-item ${theme.card} backdrop-blur-sm border rounded-2xl p-6 group transition-all duration-500 hover:shadow-2xl hover:border-pink-500/30`} style={{ opacity: 0 }}>
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
              <Printer size={24} className="text-blue-400" />
            </div>
            <h3 className={`text-lg font-bold ${theme.text.primary} font-sans`}>Print</h3>
          </div>
          <p className={`${theme.text.muted} text-sm mb-4 font-sans`}>
            Professional printing solutions for all your marketing needs
          </p>
          <ul className={`space-y-2 text-sm ${theme.text.secondary} font-sans`}>
            <li>• Brochures & flyers</li>
            <li>• Business cards & stationery</li>
            <li>• Large format printing</li>
            <li>• Custom print solutions</li>
          </ul>
        </div>

        {/* Digital Signage */}
        <div className={`portfolio-item ${theme.card} backdrop-blur-sm border rounded-2xl p-6 group transition-all duration-500 hover:shadow-2xl hover:border-pink-500/30`} style={{ opacity: 0 }}>
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
              <Monitor size={24} className="text-green-400" />
            </div>
            <h3 className={`text-lg font-bold ${theme.text.primary} font-sans`}>Digital Signage</h3>
          </div>
          <p className={`${theme.text.muted} text-sm mb-4 font-sans`}>
            Advanced IPTV and digital display solutions
          </p>
          <ul className={`space-y-2 text-sm ${theme.text.secondary} font-sans`}>
            <li>• IPTV systems & solutions</li>
            <li>• Custom digital signage</li>
            <li>• Physical store signs</li>
            <li>• Interactive shop screens</li>
          </ul>
        </div>

        {/* 3D Printing */}
        <div className={`portfolio-item ${theme.card} backdrop-blur-sm border rounded-2xl p-6 group transition-all duration-500 hover:shadow-2xl hover:border-pink-500/30`} style={{ opacity: 0 }}>
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
              <Box size={24} className="text-purple-400" />
            </div>
            <h3 className={`text-lg font-bold ${theme.text.primary} font-sans`}>3D Printing</h3>
          </div>
          <p className={`${theme.text.muted} text-sm mb-4 font-sans`}>
            Rapid prototyping and custom manufacturing solutions
          </p>
          <ul className={`space-y-2 text-sm ${theme.text.secondary} font-sans`}>
            <li>• Rapid prototyping</li>
            <li>• Custom parts & models</li>
            <li>• Product development</li>
            <li>• Manufacturing support</li>
          </ul>
        </div>

        {/* Software & Apps */}
        <div className={`portfolio-item ${theme.card} backdrop-blur-sm border rounded-2xl p-6 group transition-all duration-500 hover:shadow-2xl hover:border-pink-500/30`} style={{ opacity: 0 }}>
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
              <Smartphone size={24} className="text-orange-400" />
            </div>
            <h3 className={`text-lg font-bold ${theme.text.primary} font-sans`}>Software & Apps</h3>
          </div>
          <p className={`${theme.text.muted} text-sm mb-4 font-sans`}>
            Bespoke software solutions, hosting & custom applications
          </p>
          <ul className={`space-y-2 text-sm ${theme.text.secondary} font-sans`}>
            <li>• Custom software development</li>
            <li>• Web design & development</li>
            <li>• Mobile applications</li>
            <li>• Web hosting & reseller hosting</li>
            <li>• Hardware integration</li>
            <li>• Custom gadgets & devices</li>
          </ul>
        </div>

        {/* Call-to-Action Card */}
        <div className="portfolio-item bg-gradient-to-br from-pink-500/20 to-pink-600/20 backdrop-blur-sm border border-pink-500/30 rounded-2xl p-6 group transition-all duration-500 hover:shadow-2xl flex flex-col justify-center items-center text-center" style={{ opacity: 0 }}>
          <MessageSquare size={32} className="text-pink-400 mb-4 group-hover:scale-110 transition-transform duration-300" />
          <h3 className={`text-lg font-bold ${theme.text.primary} font-sans mb-2`}>
            Questions?<br />
            Need something else?
          </h3>
          <p className={`${theme.text.secondary} text-sm mb-4 font-sans`}>
            Contact us to discuss any questions or services - whether they're listed here or something completely different.
          </p>
          <button
            onClick={scrollToContact}
            className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-400 hover:to-pink-500 text-white px-6 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 font-sans text-sm"
          >
            Get In Touch
          </button>
        </div>
      </div>
    </div>
  );
};

// Portfolio Section (Reusable)
export const PortfolioSection = ({ 
  id, 
  title, 
  subtitle, 
  items, 
  theme, 
  isDarkMode,
  hoveredItem,
  setHoveredItem,
  currentImageIndex,
  particles,
  createParticles 
}) => {
  const [visibleItems, setVisibleItems] = useState(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const id = entry.target.dataset.itemId;
          if (entry.isIntersecting) {
            setVisibleItems(prev => new Set([...prev, id]));
          } else {
            setVisibleItems(prev => { const n = new Set(prev); n.delete(id); return n; });
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll('.portfolio-card-observer').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  return (
    <div id={id} className="mb-8 mt-16" style={{scrollMarginTop: '120px'}}>
      <div className="text-center mb-12">
        <h2 className={`section-heading section-heading-hidden text-xl sm:text-2xl ${theme.text.primary} mb-1 uppercase`}>
          {title}
        </h2>
        <p className={`text-xs ${theme.text.muted} font-sans flex items-center justify-center subtitle-animate`}>
          {subtitle}
        </p>
        <div className="h-0.5 mx-auto mt-2 rounded-full underline-animate" style={{backgroundColor: '#eb008b'}}></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {items.map((item) => (
          <div
            key={item.id}
            data-item-id={item.id}
            className={`portfolio-card-observer portfolio-item ${theme.card} backdrop-blur-sm border rounded-2xl overflow-hidden group transition-all duration-500 transform hover:scale-105 hover:shadow-2xl relative cursor-pointer`}
            style={{ 
              opacity: 0,
              borderColor: hoveredItem === item.id ? '#eb008b80' : undefined
            }}
            onMouseEnter={() => {
              setHoveredItem(item.id);
              createParticles(item.id);
            }}
            onMouseLeave={() => setHoveredItem(null)}
            onClick={() => createParticles(item.id)}
          >
            {item.logo && (
            <div className="absolute top-44 right-4 z-20">
              {item.whiteLogo ? (
                <div className="w-16 h-16 rounded-lg p-1.5 flex items-center justify-center"
                  style={{
                    background: isDarkMode
                      ? 'linear-gradient(to right, rgba(30,41,59,0.95), rgba(51,65,85,0.9))'
                      : 'linear-gradient(to right, rgba(209,213,219,0.95), rgba(229,231,235,0.9))',
                    border: isDarkMode ? '1px solid rgba(71,85,105,0.6)' : '1px solid rgba(156,163,175,0.6)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  }}
                >
                  <img
                    src={item.logo}
                    alt={`${item.title} logo`}
                    className={`w-full h-full object-contain rounded-sm bg-white ${item.whiteLogoTight ? 'p-1' : 'p-1.5'}`}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              ) : (
                <div className="w-16 h-16 rounded-lg flex items-center justify-center"
                  style={{
                    background: isDarkMode
                      ? 'linear-gradient(to right, rgba(30,41,59,0.95), rgba(51,65,85,0.9))'
                      : 'linear-gradient(to right, rgba(209,213,219,0.95), rgba(229,231,235,0.9))',
                    border: isDarkMode ? '1px solid rgba(71,85,105,0.6)' : '1px solid rgba(156,163,175,0.6)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    padding: '6px'
                  }}
                >
                  <div className="w-full h-full rounded-sm overflow-hidden flex items-center justify-center">
                    <img
                      src={item.logo}
                      alt={`${item.title} logo`}
                      className={`opacity-95 group-hover:opacity-100 transition-all duration-300 ${item.logoCover ? 'w-full h-full object-cover' : 'max-w-full max-h-full object-contain'}`}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                </div>
              )}
            </div>
            )}

            <div className="relative h-48 overflow-hidden">
              <div className="relative w-full h-full">
                {item.images.map((image, imageIndex) => (
                  <img
                    key={imageIndex}
                    src={image}
                    alt={`${item.title} ${imageIndex + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-all duration-700 ease-in-out ${
                      imageIndex === (currentImageIndex[item.id] || 0)
                        ? 'opacity-100'
                        : 'opacity-0'
                    }`}
                    style={{
                      transitionProperty: 'opacity, transform',
                      transitionDuration: '800ms',
                      transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  />
                ))}
              </div>
              <div className={`absolute inset-0 bg-gradient-to-t ${isDarkMode ? 'from-slate-900/60' : 'from-white/60'} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
              {item.copyright && (
                <div className="absolute bottom-2 left-2 flex items-center gap-1 pointer-events-none">
                  <span className="text-white text-[10px] font-sans bg-black/40 backdrop-blur-sm px-2 py-0.5 rounded-full">© Image copyright Spiral Creations</span>
                </div>
              )}
            </div>

            <div className="pt-3 pb-6 px-6 relative">
              <div className="mb-3 -mx-6 overflow-hidden">
                <div className="portfolio-title-label inline-block pl-6 pr-6 py-1 rounded-r-full font-sans font-semibold text-base tracking-wide"
                  style={{
                    background: isDarkMode
                      ? 'linear-gradient(to right, rgba(30,41,59,0.95), rgba(51,65,85,0.9))'
                      : 'linear-gradient(to right, rgba(209,213,219,0.95), rgba(229,231,235,0.9))',
                    borderTop: isDarkMode ? '1px solid rgba(71,85,105,0.6)' : '1px solid rgba(156,163,175,0.6)',
                    borderRight: isDarkMode ? '1px solid rgba(71,85,105,0.6)' : '1px solid rgba(156,163,175,0.6)',
                    borderBottom: isDarkMode ? '1px solid rgba(71,85,105,0.6)' : '1px solid rgba(156,163,175,0.6)',
                    color: isDarkMode ? 'white' : '#374151',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    transform: visibleItems.has(String(item.id)) ? 'translateX(0)' : 'translateX(-110%)',
                    opacity: visibleItems.has(String(item.id)) ? 1 : 0,
                    transition: 'transform 0.5s cubic-bezier(0.16,1,0.3,1) 0.2s, opacity 0.5s cubic-bezier(0.16,1,0.3,1) 0.2s'
                  }}
                >
                  {item.title}
                </div>
              </div>
              
              <div className={`${theme.text.muted} text-sm mb-4 font-sans`}>
                {item.description}
              </div>
              
              <div className="flex flex-wrap gap-1 mb-1">
                {item.tags.map((tag, i) => (
                  <span key={i} className={`inline-flex items-center gap-1 ${isDarkMode ? 'bg-slate-700/30 text-gray-400' : 'bg-gray-200 text-gray-600'} px-1.5 py-0.5 rounded text-[10px] font-sans`}>
                    <span style={{color: '#eb008b'}}>✓</span>{tag}
                  </span>
                ))}
              </div>

              {particles[item.id] && (
                <div className="absolute inset-0 pointer-events-none">
                  {particles[item.id].map((particle) => (
                    <div
                      key={particle.id}
                      className="particle absolute rounded-full"
                      style={{
                        left: `${particle.x}%`,
                        top: `${particle.y}%`,
                        width: `${particle.size}px`,
                        height: `${particle.size}px`,
                        backgroundColor: '#eb008b',
                        filter: 'drop-shadow(0 0 3px #eb008b)',
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};